import '../src/index.css';
import 'legacy/common/styles/style.scss';
import 'legacy/common/styles/lib/_tailwind.css';
import 'legacy/common/styles/lib/_manifest.scss';
import 'polyfills';

import Head from 'next/head';
import { AppProps } from 'next/app';
import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import xss from 'xss';

import { queryClient } from 'legacy/common/utils/react-query';
import usePageTracking from 'legacy/common/hooks/usePageTracking';
import { LAUNCHDARKLY_USER_KEYS } from 'legacy/common/constants/launchdarkly';

import { initAmplitude, initApiClient } from 'init';
import config from 'config';
import { useStore } from '../src/legacy/store';

import RedirectContainer from '../src/legacy/RedirectContainer';
import ErrorBoundary from 'legacy/components/util/ErrorBoundary/ErrorBoundary';
import UnverifiedEmailCheck from 'legacy/auth/components/unverified-email/UnverifiedEmailCheck';
import { ComponentType, ReactNode, useEffect } from 'react';
import MetaPixel from 'legacy/common/components/MetaPixel';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import WebViewWrapper from 'components/WebViewWrapper';
import { IntercomProvider } from '../src/features/intercom/context/IntercomProvider';

initAmplitude();
initApiClient();

type Props = AppProps & {
  Component: Pick<AppProps, 'Component'> & {
    getLayout?: (page: ReactNode) => typeof page;
  };
};

// This default export is required in a new `pages/_app.js` file.
const App = ({ Component, pageProps }: Props) => {
  const store = useStore(pageProps.initialReduxState);
  const { i18n } = useTranslation();
  const router = useRouter();

  usePageTracking();

  useEffect(() => {
    if (router.query.lang === 'fr') {
      localStorage.setItem('bounceSelectedLanguage', 'fr');
      i18n.changeLanguage('fr');
    }
  }, [router.query.lang, i18n]);

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  // Check if the route is a webview route.
  // This is used to determine which wrapper to use in order to remove the bloat of the app in the webview.
  const isWebviewRoute = router.pathname.startsWith('/webviews');

  return (
    <QueryClientProvider client={queryClient}>
      <MetaPixel />
      <Head>
        <title>Bounce</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
        />
        {config.HEAP_ID && (
          <script
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: `
              window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","identifyHashed","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
              heap.load("${xss(config.HEAP_ID)}"); 
              `
            }}
          />
        )}
      </Head>
      <ErrorBoundary>
        <Toaster position="top-right" reverseOrder={false} />
        <Provider store={store}>
          <div id="app-content">
            {isWebviewRoute ? (
              <WebViewWrapper Component={Component} pageProps={pageProps} />
            ) : (
              <IntercomProvider>
                {getLayout(
                  <RedirectContainer
                    Component={Component}
                    pageProps={pageProps}
                  />
                )}
              </IntercomProvider>
            )}
          </div>
          <UnverifiedEmailCheck />
        </Provider>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default withLDProvider({
  clientSideID: config.LAUNCHDARKLY_CLIENT_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false
  },
  context: {
    kind: 'user',
    key: LAUNCHDARKLY_USER_KEYS.ANONYMOUS,
    anonymous: true
  }
})(App as ComponentType<AppProps>);
